<template>
  <f7-page name="share-target" class="share-target-page withMainToolbar">
    <f7-navbar back-link>
      <f7-nav-left>
        <f7-link @click="goBack()">
          <f7-icon material="arrow_left" :size="18" />
        </f7-link>
      </f7-nav-left>
      <f7-nav-title>{{ $t("shareTarget.title") }}</f7-nav-title>
    </f7-navbar>
    <AppLayout>
      <template #main>
        <f7-block strong v-if="loading" class="share-target-loading">
          <f7-preloader />
          <p>{{ $t("shareTarget.loading") }}</p>
        </f7-block>

        <template v-else>
          <f7-card class="share-preview-card" :padding="false">
            <f7-card-content class="share-preview-content" :padding="false">
              <div v-if="shareData.files.length" class="share-preview-files">
                <div
                  v-for="(file, i) in shareData.files"
                  :key="i"
                  class="share-preview-file"
                >
                  <img
                    v-if="file.type && file.type.startsWith('image/')"
                    :src="file.uri"
                    class="share-preview-image"
                    :alt="$t('shareTarget.imageAlt')"
                  />
                  <div v-else class="share-preview-file-icon">
                    <f7-icon f7="doc" :size="28" />
                    <span class="share-preview-file-name">{{ file.name }}</span>
                  </div>
                </div>
              </div>
              <div v-if="shareData.title" class="share-preview-title">
                {{ shareData.title }}
              </div>
              <div v-if="shareData.text" class="share-preview-text">
                {{ shareData.text }}
              </div>
              <div v-if="shareData.url" class="share-preview-url">
                {{ shareData.url }}
              </div>
            </f7-card-content>
          </f7-card>

          <f7-card class="share-mode-card" :padding="false">
            <f7-card-content class="share-mode-content" :padding="false">
              <div class="share-mode-row">
                <f7-button
                  round
                  class="share-mode-btn"
                  :class="{ active: mode === 'chat' }"
                  @click="mode = 'chat'"
                >
                  <f7-icon material="chat_bubble" :size="16" />
                  <span>{{ $t("shareTarget.sendToChat") }}</span>
                </f7-button>
                <f7-button
                  round
                  class="share-mode-btn"
                  :class="{ active: mode === 'feed' }"
                  @click="mode = 'feed'"
                >
                  <span class="material-symbols-outlined news-icon">news</span>
                  <span>{{ $t("shareTarget.postToFeed") }}</span>
                </f7-button>
              </div>
            </f7-card-content>
          </f7-card>

          <f7-card v-if="mode === 'feed'" class="share-feed-card" :padding="false">
            <f7-card-content class="share-feed-content" :padding="false">
              <f7-list
                v-if="groupList.length"
                class="share-group-select"
                media-list
              >
                <f7-list-input
                  :label="$t('shareTarget.selectGroup')"
                  type="select"
                  :value="selectedFeedGroupId"
                  @change="selectedFeedGroupId = $event.target.value"
                >
                  <option value="">{{ $t("shareTarget.noGroup") }}</option>
                  <option v-for="g in groupList" :key="g.id" :value="g.id">
                    {{ g.name }}
                  </option>
                </f7-list-input>
              </f7-list>
              <f7-list>
                <f7-list-input
                  v-model="feedMessage"
                  type="textarea"
                  :placeholder="$t('shareTarget.addMessage')"
                  :rows="3"
                  resizable
                />
              </f7-list>
            </f7-card-content>
          </f7-card>

          <f7-block v-if="mode === 'feed'" class="share-send-btn-block">
            <f7-button
              fill
              round
              class="share-send-btn"
              :disabled="sending"
              @click="postToFeed()"
            >
              <f7-icon f7="paperplane" :size="16" />
              {{ $t("shareTarget.publish") }}
            </f7-button>
          </f7-block>

          <ShareToChatSheet
            v-if="mode === 'chat'"
            v-model:opened="chatSheetOpen"
            :groups="groupList"
            @selectGroup="onChatGroupSelected"
          />
        </template>

        <f7Toast
          v-if="toastMessage"
          :text="toastMessage"
          :position="toastPosition"
          :closeTimeout="3000"
        />

        <f7-login-screen
          :opened="confirmScreenOpen"
          @loginscreen:closed="confirmScreenOpen = false"
          class="share-confirm-screen"
        >
          <f7-page class="share-confirm-page">
            <f7-navbar>
              <f7-nav-title>{{ $t("shareTarget.confirm") }}</f7-nav-title>
            </f7-navbar>
            <AppLayout>
              <template #main>
                <f7-card class="share-confirm-content" :padding="false">
                  <f7-card-content class="share-confirm-content-inner" :padding="false">
                    <div class="share-confirm-icon">
                      <f7-icon
                        f7="checkmark_circle_fill"
                        :size="56"
                        color="green"
                      />
                    </div>
                    <h3>{{ $t("shareTarget.sharedSuccessfully") }}</h3>
                    <p>
                      {{ $t("shareTarget.sharedTo") }} {{ confirmTargetName }}
                    </p>
                  </f7-card-content>
                </f7-card>
                <f7-block class="share-confirm-actions">
                  <f7-button
                    fill
                    raised
                    round
                    @click="goToChat()"
                    v-if="confirmChatId"
                  >
                    {{ $t("shareTarget.goToChat") }}
                  </f7-button>
                  <f7-button outline round @click="goBack()">
                    {{ $t("shareTarget.done") }}
                  </f7-button>
                </f7-block>
              </template>
            </AppLayout>
          </f7-page>
        </f7-login-screen>
      </template>
    </AppLayout>
  </f7-page>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { f7 } from "framework7-vue";
import AppLayout from "../components/common/AppLayout.vue";
import { useMessageStore } from "../js/pinia-stores/message.js";
import { useFeedStore } from "../js/pinia-stores/feed.js";
import { useAuthStore } from "../js/pinia-stores/auth.js";
import { useKeycloakStore } from "../js/pinia-stores/keycloak.js";
import { useAccountStore } from "../js/pinia-stores/account.js";
import ShareToChatSheet from "../components/tamtam/ShareToChatSheet.vue";
import i18n from "../js/i18n.js";
const props = defineProps({
  f7route: Object,
  f7router: Object,
});
const { t } = i18n.global;
const messageStore = useMessageStore();
const feedStore = useFeedStore();
const authStore = useAuthStore();
const keycloakStore = useKeycloakStore();
const accountStore = useAccountStore();

const loading = ref(true);
const mode = ref("chat");
const sending = ref(false);
const chatSheetOpen = ref(false);
const feedMessage = ref("");
const selectedFeedGroupId = ref("");
const toastMessage = ref("");
const toastPosition = ref("bottom");
const confirmScreenOpen = ref(false);
const confirmTargetName = ref("");
const confirmChatId = ref(null);
const confirmGroupType = ref("");

const shareData = ref({ title: "", text: "", url: "", files: [] });

const currentUserId = computed(
  () =>
    keycloakStore.subject ||
    keycloakStore.tokenParsed?.sub ||
    accountStore.userAccount?.userId ||
    "",
);

const groupList = computed(() => messageStore.groupList || []);

function goBack() {
  try {
    props.f7router.back();
  } catch (_) {
    window.history.back();
  }
}

function showToast(msg) {
  toastMessage.value = msg;
  setTimeout(() => {
    toastMessage.value = "";
  }, 3000);
}

function getCounterpart(chatGroup) {
  if (!chatGroup.groupParticipantList) return null;
  return chatGroup.groupParticipantList.find(
    (p) => p.userId && p.userId !== currentUserId.value,
  );
}

async function loadFromCache() {
  try {
    const cache = await caches.open("share-payload-cache");
    const response = await cache.match("latest-share");
    if (response) {
      const data = await response.json();
      shareData.value = {
        title: data.title || "",
        text: data.text || "",
        url: data.url || "",
        files: (data.files || []).map((f) => ({
          name: f.name,
          type: f.type,
          uri: f.uri,
        })),
      };
      await cache.delete("latest-share");
    }
  } catch (e) {
    console.warn("share-target: failed to read from SW cache", e);
  }
}

function loadFromQuery() {
  const q = props.f7route?.query || {};
  shareData.value = {
    title: q.title || "",
    text: q.text || "",
    url: q.url || "",
    files: [],
  };
  for (let i = 0; i < 10; i++) {
    const fileUri = q[`fileUri${i}`];
    const fileName = q[`fileName${i}`];
    const fileMime = q[`fileMime${i}`];
    if (!fileUri) break;
    shareData.value.files.push({
      name: fileName || "shared-file",
      type: fileMime || "application/octet-stream",
      uri: fileUri,
    });
  }
}

function loadFromNativeIntent() {
  const q = props.f7route?.query || {};
  shareData.value = {
    title: q.title || "",
    text: q.description || "",
    url: q.url || "",
    files: [],
  };
  if (q.url && q.type && q.type !== "text/plain") {
    shareData.value.files.push({
      name: q.title || "shared-file",
      type: q.type,
      uri: q.url,
    });
  }
}

function buildMessageText() {
  const parts = [];
  if (shareData.value.title) parts.push(shareData.value.title);
  if (shareData.value.text) parts.push(shareData.value.text);
  if (shareData.value.url) parts.push(shareData.value.url);
  return parts.join("\n");
}

async function onChatGroupSelected(group) {
  sending.value = true;
  try {
    const counterpart = getCounterpart(group);
    const isPersonal = group.groupType === "PERSONAL";
    const receiverId = isPersonal ? counterpart?.userId || group.id : group.id;
    const receiverName = isPersonal
      ? counterpart?.name || counterpart?.username || group.name
      : group.name;
    const chatUser = {
      userId: currentUserId.value,
      name: authStore.profile?.name || authStore.user?.name || "",
      username: authStore.profile?.username || "",
      phoneNumber: authStore.profile?.phoneNumber || "",
    };

    const messageText = buildMessageText();
    const message = {
      text: messageText,
      senderId: currentUserId.value,
      senderName: chatUser.name.split(" ")[0] || chatUser.username || "",
      sentAt: new Date().toISOString(),
      readAt: "",
      attachment: "",
      attachments: [],
      replyingTo: "",
      receiverId,
      receiverName,
      groupType: group.groupType,
      type: "sent",
      status: "sent",
      chatGroup: { id: group.id },
      msgType: "text",
    };

    await messageStore.sendChatMessage(message);

    confirmTargetName.value = group.name;
    confirmChatId.value = group.id;
    confirmGroupType.value = group.groupType;
    confirmScreenOpen.value = true;
  } catch (e) {
    console.error("share-target: send to chat failed", e);
    showToast(f7.params.get?.("shareFailed") || "Failed to send");
  } finally {
    sending.value = false;
  }
}

async function postToFeed() {
  if (!selectedFeedGroupId.value) {
    showToast(t("common.selectAGroup"));
    return;
  }
  sending.value = true;
  try {
    const group = groupList.value.find(
      (g) => g.id === selectedFeedGroupId.value,
    );
    const userName = authStore.profile?.name || authStore.user?.name || "";
    const userAvatar = authStore.profile?.avatar || "";

    const postData = {
      text: feedMessage.value || buildMessageText(),
      groupId: selectedFeedGroupId.value,
      groupName: group?.name || "",
      authorName: userName,
      authorAvatar: userAvatar,
      author: {
        userId: currentUserId.value,
        name: userName,
        avatar: userAvatar,
      },
      type: "POST",
      timeAgo: t("common.justNow"),
      reactions: [],
      comments: [],
      commentsCount: 0,
    };

    await feedStore.createPost(postData);

    confirmTargetName.value = group?.name || "feed";
    confirmChatId.value = null;
    confirmGroupType.value = "";
    confirmScreenOpen.value = true;
  } catch (e) {
    console.error("share-target: post to feed failed", e);
    showToast("Failed to post");
  } finally {
    sending.value = false;
  }
}

function goToChat() {
  confirmScreenOpen.value = false;
  if (confirmChatId.value) {
    props.f7router.navigate(
      `/message/${confirmChatId.value}/${confirmGroupType.value}`,
    );
  } else {
    goBack();
  }
}

onMounted(async () => {
  await loadFromCache();
  if (!shareData.value.title && !shareData.value.text && !shareData.value.url) {
    loadFromQuery();
  }
  if (!shareData.value.title && !shareData.value.text && !shareData.value.url) {
    loadFromNativeIntent();
  }
  loading.value = false;

  if (groupList.value.length === 0) {
    try {
      await messageStore.fetchGroups();
    } catch (_) {}
  }

  if (!messageStore.group || !messageStore.group.id) {
    try {
      messageStore.initSocket();
    } catch (_) {}
  }

  setTimeout(() => {
    chatSheetOpen.value = true;
  }, 300);
});
</script>

<style scoped>
.share-target-page {
  --f7-page-bg-color: #f5f5f5;
}
.share-target-loading {
  text-align: center;
  padding: 40px 16px;
}
.share-preview-card {
  margin: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  border-radius: 12px;
  background: var(--f7-card-bg-color, #fff);
  border: 1px solid var(--f7-list-item-border-color, var(--f7-list-item-border-color, #e0e0e0));
}
.share-preview-content {
  position: relative;
  padding: 14px;
}
.share-preview-files {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 10px;
}
.share-preview-image {
  width: 100%;
  max-height: 200px;
  object-fit: cover;
  border-radius: 8px;
}
.share-preview-file-icon {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: var(--f7-page-bg-color, #f5f5f5);
  border-radius: 8px;
  font-size: 12px;
}
.share-preview-file-name {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.share-preview-title {
  font-weight: 600;
  font-size: 15px;
  margin-bottom: 4px;
}
.share-preview-text {
  font-size: 13px;
  color: #666;
  margin-bottom: 4px;
  white-space: pre-line;
}
.share-preview-url {
  font-size: 12px;
  color: var(--f7-theme-color, #3b4fca);
  word-break: break-all;
}
.share-mode-card {
  margin: 0 16px 8px;
  box-shadow: none;
  border-radius: 12px;
}
.share-mode-content {
  position: relative;
  padding: 12px;
}
.share-mode-row {
  display: flex;
  gap: 10px;
}
.share-mode-btn {
  flex: 1;
  --f7-button-border-radius: 10px;
  --f7-button-height: 44px;
  --f7-button-outline-border-color: var(--f7-list-item-border-color, var(--f7-list-item-border-color, #e0e0e0));
  --f7-button-text-color: #666;
  --f7-button-bg-color: #fff;
  font-size: 13px;
  font-weight: 500;
  gap: 6px;
}
.share-mode-btn.active {
  --f7-button-outline-border-color: var(--f7-theme-color, #3b4fca);
  --f7-button-bg-color: rgba(59, 79, 202, 0.06);
  --f7-button-text-color: var(--f7-theme-color, #3b4fca);
}
.news-icon {
  font-variation-settings: 'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 16;
}
.share-feed-card {
  margin: 0 16px 8px;
  box-shadow: none;
  border-radius: 12px;
}
.share-feed-content {
  position: relative;
  padding: 16px;
}
.share-group-select {
  margin-top: 0;
}
.share-send-btn-block {
  padding: 8px 16px 24px;
}
.share-send-btn {
  width: 100%;
}
.share-confirm-page {
  --f7-page-bg-color: #fff;
}
.share-confirm-content {
  margin: 16px;
  box-shadow: none;
  border-radius: 12px;
  text-align: center;
}
.share-confirm-content-inner {
  position: relative;
  padding: 32px 16px;
}
.share-confirm-icon {
  margin-bottom: 16px;
}
.share-confirm-content h3 {
  margin: 0 0 8px;
  font-size: 18px;
}
.share-confirm-content p {
  color: #666;
  font-size: 14px;
}
.share-confirm-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 0 16px 16px;
}
</style>
